import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import TypographyContainer from "../../ui/layout/page/typography-container/typography-base-container";
import ApplicationPageContent from "../../ui/layout/page/content/application";
import { useMessageSource } from "react-message-source";
import TypographyItemContainer from "../../ui/layout/page/typography-container/typography-item-container";
import Typography from "../../ui/widgets/typography";
import NavigationBar from "../../ui/layout/page/content/footer/navigation-bar";
import { CompanyDocumentUpload } from "./company-document-upload";
import { PersonDocumentUpload } from "./person-document-upload";
import ApplicationAPI from "../../api/commercial/application";
import {
  ApplicationAttachmentsDocument,
  AttachmentUploadConfigurationDocument,
  BasicAccountApplicationDocument,
  CardApplicationDocument,
} from "../../api/commercial/generated-types";
import { ErrorContext } from "../../route/error/provider";
import FlowHeader from "../../ui/widgets/flow-header";
import { ApplicationFlows } from "../../route/application/authenticated/application-flow/types";
import { usePageTitle } from "../shared/hooks/usePageTitle";
import { ContinueWithoutUploadingModal } from "./continue-without-uploading-modal";
import { UploadApi } from "../../api/commercial/document-upload/api";
import logging from "../../util/logging";
import { UploadHelper } from "./upload-helper";
import ProductImage from "../shared/components/product-image";
import { ProductContext } from "../../context/product-context/productContext";
import { CustomizationCardService } from "./customization-card/customization-card-service";
import CountableStep from "../../ui/widgets/countable-step";
import PostDocuments from "./post-documents";
import { Accept } from "react-dropzone";

const log = logging("pages/document-upload/adapter");

interface IAttachmentUploadContext extends AttachmentUploadConfigurationDocumentSanitized {
  syncAttachmentStateWithBackend: () => void;
}

export const AttachmentUploadContext = createContext<
  IAttachmentUploadContext | undefined
>(undefined);

export interface DocumentUploadAdapterProps {
  flowType: ApplicationFlows;
  cardApplication: CardApplicationDocument;
  basicAccountApplication: BasicAccountApplicationDocument;
  triggerReload: () => void;
}

export interface AttachmentUploadConfigurationDocumentSanitized extends AttachmentUploadConfigurationDocument {
  dropzoneAllowedMimeTypes: Accept;
}

export const DocumentUploadAdapter = ({
  flowType,
  cardApplication,
  basicAccountApplication,
  triggerReload,
}: DocumentUploadAdapterProps): React.JSX.Element => {
  const { getMessage } = useMessageSource();
  const { setError } = useContext(ErrorContext);
  const { selectedProduct } = useContext(ProductContext);

  const [attachmentProperties, setAttachmentProperties] = useState<
    AttachmentUploadConfigurationDocumentSanitized | undefined
  >(undefined);

  const [applicationAttachments, setApplicationAttachments] = useState<
    ApplicationAttachmentsDocument | undefined
  >(undefined);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const isBaaUploadAttachmentListEmpty =
    !applicationAttachments?.basicAccountApplicationAttachmentsDocument ||
    applicationAttachments.basicAccountApplicationAttachmentsDocument.length ===
      0;
  // Authenticity proof documents cannot be uploaded
  const isCaUploadAttachmentListEmpty =
    !applicationAttachments?.cardApplicationAttachmentsDocument ||
    (applicationAttachments.cardApplicationAttachmentsDocument.length <= 1 &&
      applicationAttachments.cardApplicationAttachmentsDocument.some(
        (doc) => doc.attachmentDocumentType === "AUTHENTICITY_PROOF",
      ));

  const onlyEmptyAttachmentBAACompany =
    basicAccountApplication?.requiredCompanyAttachments?.filter(
      (baaCompanyAttachment) => {
        return !UploadHelper.isFileTypeUploaded(
          baaCompanyAttachment.domain,
          applicationAttachments,
        );
      },
    );

  const onlyEmptyAttachmentBAAPerson =
    basicAccountApplication?.requiredPersonAttachments?.filter(
      (baaPersonAttachment) => {
        return !UploadHelper.isFileTypeUploaded(
          baaPersonAttachment.domain,
          applicationAttachments,
        );
      },
    );

  const onlyEmptyAttachmentCA = cardApplication?.requiredAttachments?.filter(
    (caAttachment) => {
      return !UploadHelper.isFileTypeUploaded(
        caAttachment.domain,
        applicationAttachments,
      );
    },
  );

  const isUploadListNotEmpty = !(
    isBaaUploadAttachmentListEmpty && isCaUploadAttachmentListEmpty
  );
  useEffect(() => {
    UploadApi.properties()
      .then((properties) => {
        let dropzoneAllowedMimeTypes = {};
        properties.allowedMimeTypes.forEach((allowedMimeType) => {
          dropzoneAllowedMimeTypes = {
            [allowedMimeType]: ["." + allowedMimeType.split("/")[1]],
            ...dropzoneAllowedMimeTypes,
          };
        });
        setAttachmentProperties({ ...properties, dropzoneAllowedMimeTypes });
      })
      .catch((e) => {
        log.error(
          "Failed fetching document upload properties from backend. Reason: " +
            e.message,
        );
        setAttachmentProperties(undefined);
      });
  }, []);

  const syncAttachmentStateWithBackend = useCallback(async () => {
    try {
      const applicationAttachmentsDocument = await UploadApi.sync();
      setApplicationAttachments(applicationAttachmentsDocument);
    } catch (e) {
      log.error(
        "Failed fetching attachment upload information from backend. Reason: " +
          (e as Error).message,
      );
    }
  }, []);

  useEffect(() => {
    syncAttachmentStateWithBackend();
  }, [
    cardApplication.applicationRef,
    basicAccountApplication?.applicationRef,
    syncAttachmentStateWithBackend,
  ]);

  const onNext = async (): Promise<void> => {
    if (
      UploadHelper.isUploadDirty(applicationAttachments) === false &&
      !(isBaaUploadAttachmentListEmpty && isCaUploadAttachmentListEmpty)
    ) {
      setIsModalOpen(true);
      return;
    }
    try {
      await ApplicationAPI.completeCardApplication(
        cardApplication.applicationRef,
        selectedProduct.productRef,
      );
    } catch (e) {
      setError(e);
    }
    triggerReload();
  };

  const isCustomizationComponentVisible = () => {
    return (
      basicAccountApplication?.customizedCard &&
      basicAccountApplication.customizedCard !== "NONE" &&
      selectedProduct?.hasFeature("PICTURE_OR_LOGO_ON_CARD")
    );
  };

  return (
    <>
      {isModalOpen && (
        <ContinueWithoutUploadingModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          applicationRef={cardApplication.applicationRef}
          triggerReload={triggerReload}
        />
      )}
      <FlowHeader
        image={<ProductImage product={selectedProduct?.productRef} />}
        title={usePageTitle(flowType)}
        subTitle={cardApplication.company.name}
      />
      <ApplicationPageContent>
        {isUploadListNotEmpty && (
          <>
            <CountableStep number={3}>
              <TypographyContainer>
                <TypographyItemContainer>
                  <Typography variant="h3">
                    {getMessage("DocumentUpload.title")}
                  </Typography>
                </TypographyItemContainer>
              </TypographyContainer>
            </CountableStep>
          </>
        )}

        {UploadHelper.areAttachmentsReadyForRender(
          attachmentProperties,
          applicationAttachments,
        ) && (
          <AttachmentUploadContext.Provider
            value={{
              ...attachmentProperties,
              syncAttachmentStateWithBackend,
            }}
          >
            {!isBaaUploadAttachmentListEmpty && (
              <CompanyDocumentUpload
                basicAccountApplication={basicAccountApplication}
                companyAttachments={
                  applicationAttachments.basicAccountApplicationAttachmentsDocument
                }
              />
            )}
            {!isCaUploadAttachmentListEmpty && (
              <PersonDocumentUpload
                cardApplication={cardApplication}
                personAttachments={
                  applicationAttachments.cardApplicationAttachmentsDocument
                }
              />
            )}
          </AttachmentUploadContext.Provider>
        )}

        {isCustomizationComponentVisible() && (
          <>
            <CustomizationCardService
              basicAccountApplication={basicAccountApplication}
            />

            <hr />
          </>
        )}
        <PostDocuments
          isUploadListNotEmpty={isUploadListNotEmpty}
          requiredPersonAttachmentsBAA={onlyEmptyAttachmentBAAPerson}
          requiredCompanyAttachmentsBAA={onlyEmptyAttachmentBAACompany}
          requiredAttachmentsCA={onlyEmptyAttachmentCA}
        />

        <NavigationBar
          isSubmitting={false}
          onNext={onNext}
          nextButtonText={getMessage("Onboarding.finish")}
        />
      </ApplicationPageContent>
    </>
  );
};
